import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import { motion } from 'framer-motion';

function Home() {
  const { t } = useTranslation();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const videoRef = useRef(null);

  const testimonials = [
    { text: t('home.testimonial1'), author: 'Ivan, Kampala' },
    { text: t('home.testimonial2'), author: 'Ritah, Nairobi' },
  ];

  // Testimonial rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Video visibility control
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  // Animations
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
  };

  const heroTextVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.9, ease: 'easeOut', delay: 0.2 },
    },
  };

  return (
    <div className="overflow-x-hidden">
      <Helmet>
        <title>{t('home.seoTitle')}</title>
        <meta name="description" content={t('home.seoDescription')} />
      </Helmet>

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative h-[520px] md:h-[620px] lg:h-[720px] flex items-end md:items-center justify-center text-white overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/assets/images/hero-poster.jpg"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/assets/images/hero2.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20" />

        <motion.div
          className="relative z-10 w-full max-w-5xl mx-auto px-6 pb-16 md:pb-0 md:text-center"
          initial="hidden"
          animate="visible"
          variants={heroTextVariants}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight drop-shadow-lg">
            {t('home.heroTitle')}
          </h1>
          <p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl mx-auto drop-shadow-md">
            {t('home.heroSubtitle')}
          </p>
          <Link
            to="/donate"
            className="inline-block bg-accent hover:bg-yellow-500 text-white px-8 py-3.5 rounded-xl text-lg font-semibold 
                       transition-all duration-300 hover:scale-105 shadow-xl"
          >
            {t('home.donateButton')}
          </Link>
        </motion.div>
      </section>

      {/* ==================== IMPACT SECTION ==================== */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('home.impactTitle')}</h2>
            <div className="w-20 h-1 bg-accent mx-auto rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Card 1 */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center hover:bg-white/15 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-accent/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <VisibilitySensor partialVisibility>
                {({ isVisible }) => (
                  <p className="text-4xl md:text-5xl font-bold mb-2">
                    {isVisible ? <CountUp end={1000} duration={2.5} separator="," suffix="+" /> : '0+'}
                  </p>
                )}
              </VisibilitySensor>
              <p className="text-lg text-white/90">{t('home.impactPeople')}</p>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center hover:bg-white/15 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-accent/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <VisibilitySensor partialVisibility>
                {({ isVisible }) => (
                  <p className="text-4xl md:text-5xl font-bold mb-2">
                    {isVisible ? <CountUp end={5} duration={2.5} suffix="+" /> : '0+'}
                  </p>
                )}
              </VisibilitySensor>
              <p className="text-lg text-white/90">{t('home.impactCrusades')}</p>
            </motion.div>

            {/* Card 3 */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center hover:bg-white/15 transition-all duration-300 hover:-translate-y-2"
            >
              <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-accent/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <VisibilitySensor partialVisibility>
                {({ isVisible }) => (
                  <p className="text-4xl md:text-5xl font-bold mb-2">
                    {isVisible ? <CountUp end={5} duration={2.5} /> : '0'}
                  </p>
                )}
              </VisibilitySensor>
              <p className="text-lg text-white/90">{t('home.impactCountries')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== WELCOME SECTION ==================== */}
      <section className="bg-secondary py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="order-first lg:order-last">
                <img
                  src="/assets/images/hero.jpeg"
                  alt={t('home.welcomeTitle')}
                  className="w-full h-[340px] md:h-[400px] object-cover rounded-2xl shadow-xl"
                  onError={(e) => (e.target.src = 'https://via.placeholder.com/600x400?text=NAOGM+Outreach')}
                />
              </div>

              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-3">
                    {t('home.welcomeTitle')}
                  </h2>
                  <div className="w-16 h-1 bg-accent rounded-full mb-6"></div>
                  <p className="text-lg text-textPrimary/90 leading-relaxed">
                    {t('home.welcomeText')}
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-textPrimary mb-3">
                    {t('home.visionTitle')}
                  </h3>
                  <p className="text-lg text-textPrimary/90 leading-relaxed">
                    {t('home.visionText')}
                  </p>
                </div>

                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 rounded-xl text-lg font-semibold 
                             hover:bg-blue-700 transition-all duration-300 hover:gap-3 shadow-md"
                >
                  {t('home.welcomeButton')}
                  <span>→</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== FEATURED EVENT ==================== */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-3">
              {t('home.featuredEventTitle')}
            </h2>
            <div className="w-20 h-1 bg-accent mx-auto rounded-full"></div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-secondary border border-gray-100 rounded-2xl p-8 md:p-10 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <span className="inline-block bg-accent/15 text-accent text-sm font-semibold px-3 py-1 rounded-full mb-3">
                    Upcoming
                  </span>
                  <h3 className="text-2xl font-bold text-textPrimary mb-2">
                    {t('home.eventTitle')}
                  </h3>
                  <p className="text-textPrimary/80 leading-relaxed">
                    {t('home.eventDetails')}
                  </p>
                </div>
                <Link
                  to="/events"
                  className="shrink-0 bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-300 text-center"
                >
                  {t('home.eventButton')}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section className="bg-secondary py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-3">
              {t('home.testimonialsTitle')}
            </h2>
            <div className="w-20 h-1 bg-accent mx-auto rounded-full"></div>
          </motion.div>

          <motion.div
            key={currentTestimonial}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg relative">
              <div className="text-6xl text-accent/20 absolute top-6 left-8 font-serif">“</div>
              
              <p className="text-xl md:text-2xl text-textPrimary leading-relaxed italic relative z-10 mb-8">
                {testimonials[currentTestimonial].text}
              </p>
              
              <p className="font-semibold text-primary text-lg">
                 {testimonials[currentTestimonial].author}
              </p>
            </div>

            {/* Dots */}
            <div className="flex justify-center mt-8 gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    currentTestimonial === index ? 'w-8 bg-primary' : 'w-3 bg-gray-300'
                  }`}
                  aria-label={`View testimonial ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== CALL TO ACTION ==================== */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary to-blue-800"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {t('home.ctaTitle')}
            </h2>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10">
              Join us in making a lasting impact across nations.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/events"
                className="bg-white text-primary px-8 py-4 rounded-xl text-lg font-semibold 
                           hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105"
              >
                {t('home.ctaEvents')}
              </Link>
              <Link
                to="/donate"
                className="bg-accent text-white px-8 py-4 rounded-xl text-lg font-semibold 
                           hover:bg-yellow-500 transition-all duration-300 shadow-lg hover:scale-105"
              >
                {t('home.ctaDonate')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Home;